import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  styled as muiStyled,
  Step,
  StepButton,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { BreadcrumbsPath } from "components/commons/BreadscrumbsPath";
import { type ChangeEvent, useState } from "react";
import { authenNavigate } from "service/utils/authen-navigate";
import {
  Eula,
  ExportControlContent,
  PrivacyPolicy,
  welcomeToImagio,
} from "./OnboardingStepContent";

const steps = ["Export Control", "Privacy Policy", "EULA"];

const BackButton = muiStyled(Button)(() => ({
  color: "#000",
  borderRadius: "2px",
  padding: "4px 15px",
  height: "32px",
  width: "64px",
  backgroundColor: "#fff",
  marginRight: "10px",
  border: "1px solid #D9D9D9",
  background: "#fff",
  boxShadow: "0px 2px 0px 0px rgba(0, 0, 0, 0.02)",
  "&:hover": {
    color: "#000",
    backgroundColor: "#fff",
  },
}));

const StateButton = muiStyled(Button)(() => ({
  color: "#fff",
  borderRadius: "2px",
  padding: "4px 15px",
  height: "32px",
  minWidth: "64px",
  maxWidth: "156px",
  backgroundColor: "#009FE4",
  whiteSpace: "nowrap",
  boxShadow: "0px 2px 0px 0px rgba(0, 0, 0, 0.04)",
  "&:hover": {
    color: "#fff",
    backgroundColor: "#009FE4",
  },
}));

const TextFieldCustom = muiStyled(TextField)(({ theme }) => ({
  color: "rgba(0, 0, 0, 0.60)",
  borderRadius: "2px",
  height: "40px",
  padding: "8px 0",
  width: "100%",
  fontSize: "var(--body-b2-14-regular-size, 14px)",
  [theme.breakpoints.up("sm")]: {
    minWidth: "298px",
    width: "auto",
  },
  "& .MuiInputBase-root": {
    height: "40px",
    borderRadius: "2px",
    padding: "0",
  },
  "& .MuiInputLabel-root": {
    top: "5px",
  },
}));

interface OnboardingState {
  region: string;
  industry: string;
  jobTitle: string;
  city: string;
  company: string;
}

const defaultOnboarding: OnboardingState = {
  region: "",
  industry: "",
  jobTitle: "",
  city: "",
  company: "",
};

export const Onboarding = () => {
  authenNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState<{
    [k: number]: boolean;
  }>({});
  const [firstStepCheck, setFirstStepCheck] = useState<boolean>(false);
  const [secondStepCheck, setSecondStepCheck] = useState<boolean>(false);
  const [thirdStepCheck, setThirdStepCheck] = useState<boolean>(false);
  const [isWelcomeCheck, setIsWelcomeCheck] = useState<boolean>(false);

  const [onboardingWelcome, setOnboardingWelcome] =
    useState<OnboardingState>(defaultOnboarding);

  const isValidFormData = (data: OnboardingState, isCheck: boolean) => {
    const isFormData =
      data.region !== "" &&
      data.industry !== "" &&
      data.jobTitle !== "" &&
      data.city !== "" &&
      data.company !== "";
    if (isCheck && isFormData) {
      return false;
    } else {
      return true;
    }
  };

  const totalSteps = () => {
    return steps.length;
  };

  const completedSteps = () => {
    return Object.keys(completed).length;
  };

  const isLastStep = () => {
    return activeStep === totalSteps() - 1;
  };

  const allStepsCompleted = () => {
    return completedSteps() === totalSteps();
  };

  const handleNext = () => {
    const newActiveStep =
      isLastStep() && !allStepsCompleted()
        ? steps.findIndex((_step, i) => !(i in completed))
        : activeStep + 1;
    setActiveStep(newActiveStep);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleComplete = () => {
    setCompleted({
      ...completed,
      [activeStep]: true,
    });
    handleNext();
  };

  // const handleReset = () => {
  //     setActiveStep(0);
  //     setCompleted({});
  // };

  const getDisableState = (step: number) => {
    if (step === 0 && firstStepCheck) {
      return false;
    } else if (step === 1 && secondStepCheck) {
      return false;
    } else if (step === 2 && thirdStepCheck) {
      return false;
    } else if (firstStepCheck && secondStepCheck && thirdStepCheck) {
      return false;
    } else {
      return true;
    }
  };

  const handleTextFieldChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const { name, value } = e.target;
    setOnboardingWelcome((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const submitOnboarding = () => {
    alert("Waiting for submit API");
  };

  return (
    <Grid
      container
      mt={0}
      mb={0}
      rowSpacing={1.5}
      columnSpacing={4}
      sx={{
        backgroundColor: "#fff",
        padding: { xs: "16px", sm: "24px 32px", md: "24px 96px" },
      }}
    >
      {!allStepsCompleted() && (
        <Grid size={{ xs: 12, md: 12, xl: 12 }}>
          <BreadcrumbsPath pathName="Imagio® Usage Agreement" />
        </Grid>
      )}
      <Grid
        size={{ xs: 12, md: 12, xl: 12 }}
        sx={{ "&.MuiGrid-item": { paddingTop: 0 } }}
      >
        <Box sx={{ width: "100%", maxWidth: "612px", margin: "auto" }}>
          {!allStepsCompleted() && (
            <>
              <Typography
                sx={{
                  mt: "24px",
                  mb: "24px",
                  fontSize: "var(--heading-h3-24-regular-size, 24px)",
                }}
                variant="h5"
              >
                Imagio® Usage Agreement
              </Typography>
              <Stepper
                sx={{ marginBottom: "42px" }}
                nonLinear
                activeStep={activeStep}
              >
                {steps.map((label, index) => (
                  <Step key={label} completed={completed[index]}>
                    <StepButton color="inherit">{label}</StepButton>
                  </Step>
                ))}
              </Stepper>
            </>
          )}

          <div>
            {allStepsCompleted() ? (
              <>
                <Typography
                  sx={{
                    mt: "48px",
                    mb: "42px",
                    fontSize: "var(--heading-h3-24-regular-size, 24px)",
                  }}
                  variant="h5"
                >
                  Welcome to Imagio®
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "16px",
                  }}
                >
                  <TextFieldCustom
                    required
                    id="txfRegion"
                    label="Region"
                    name="region"
                    onChange={handleTextFieldChange}
                  />
                  <TextFieldCustom
                    required
                    id="txfCity"
                    label="City"
                    name="city"
                    onChange={handleTextFieldChange}
                  />
                  <TextFieldCustom
                    required
                    id="txfIndustry"
                    label="Industry"
                    name="industry"
                    onChange={handleTextFieldChange}
                  />
                  <TextFieldCustom
                    required
                    id="txfCompany"
                    label="Company"
                    name="company"
                    onChange={handleTextFieldChange}
                  />
                  <TextFieldCustom
                    required
                    id="txfJobTitle"
                    label="Job Title"
                    name="jobTitle"
                    onChange={handleTextFieldChange}
                  />
                </Box>
                <Box
                  sx={{ width: "100%", maxWidth: "612px", display: "flex" }}
                  mt="42px"
                >
                  <Checkbox
                    sx={{ width: "42px", height: "42px" }}
                    checked={isWelcomeCheck}
                    onChange={() => setIsWelcomeCheck(!isWelcomeCheck)}
                  />
                  {welcomeToImagio()}
                </Box>

                <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                  <StateButton
                    sx={{
                      backgroundColor: `${isValidFormData(onboardingWelcome, isWelcomeCheck) ? "#d5d5d5" : "#009FE4"}`,
                    }}
                    disabled={isValidFormData(
                      onboardingWelcome,
                      isWelcomeCheck,
                    )}
                    onClick={submitOnboarding}
                  >
                    Start Using Imagio®
                  </StateButton>
                </Box>
              </>
            ) : (
              <>
                {activeStep === 0 && (
                  <>
                    {ExportControlContent()}
                    <FormGroup sx={{ marginTop: "42px", marginBottom: "36px" }}>
                      <FormControlLabel
                        sx={{
                          fontSize: "var(--body-b2-14-regular-size, 14px)",
                        }}
                        control={
                          <Checkbox
                            checked={firstStepCheck}
                            onChange={() => setFirstStepCheck(!firstStepCheck)}
                          />
                        }
                        label="I agree to fully comply with all export laws and regulations"
                      />
                    </FormGroup>
                  </>
                )}
                {activeStep === 1 && (
                  <>
                    {PrivacyPolicy()}
                    <FormGroup sx={{ marginTop: "42px", marginBottom: "36px" }}>
                      <FormControlLabel
                        sx={{
                          fontSize: "var(--body-b2-14-regular-size, 14px)",
                        }}
                        control={
                          <Checkbox
                            checked={secondStepCheck}
                            onChange={() =>
                              setSecondStepCheck(!secondStepCheck)
                            }
                          />
                        }
                        label="I have read and accept the Privacy Policy"
                      />
                    </FormGroup>
                  </>
                )}
                {activeStep === 2 && (
                  <>
                    {Eula()}
                    <FormGroup sx={{ marginTop: "42px", marginBottom: "36px" }}>
                      <FormControlLabel
                        sx={{
                          fontSize: "var(--body-b2-14-regular-size, 14px)",
                        }}
                        control={
                          <Checkbox
                            checked={thirdStepCheck}
                            onChange={() => setThirdStepCheck(!thirdStepCheck)}
                          />
                        }
                        label="I have read and accept the End-User License Agreement"
                      />
                    </FormGroup>
                  </>
                )}
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                  <BackButton
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    sx={{ mr: 1 }}
                  >
                    Back
                  </BackButton>
                  <StateButton
                    sx={{
                      backgroundColor: `${getDisableState(activeStep) ? "#d5d5d5" : "#009FE4"}`,
                    }}
                    disabled={getDisableState(activeStep)}
                    onClick={handleComplete}
                  >
                    {completedSteps() === totalSteps() - 1
                      ? "Complete"
                      : "Next"}
                  </StateButton>
                </Box>
              </>
            )}
          </div>
        </Box>
      </Grid>
    </Grid>
  );
};
